import { Dispatch, JSX, SetStateAction } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import VerifyInput from "react-verification-input";

import { ProfileForm } from "@/pages/gate/ProfilesPage";

import Button from "@/components/buttons/Button.tsx";
import UpdateProfileLayout from "@/components/layout/UpdateProfileLayout.tsx";

import useBeforeUnload from "@/hooks/useBeforeUnload.ts";
import { cn } from "@/utils/cn.ts";

import { ProfileTabs } from "../profiles/types";

const SetCodeToProfile = ({ setTab }: { setTab: Dispatch<SetStateAction<ProfileTabs>> }): JSX.Element => {
  const { t } = useTranslation();
  useBeforeUnload(true);
  const {
    control,
    handleSubmit,
    formState: { errors },
    resetField,
  } = useFormContext<ProfileForm>();

  const onBack = () => {
    resetField("pin_code");
    setTab(ProfileTabs.ADD_PROFILE);
  };

  const onSubmit = handleSubmit(() => {
    setTab(ProfileTabs.ADD_PROFILE);
  });

  return (
    <UpdateProfileLayout title={t("Profile.set-pin-code")} onBackClick={onBack}>
      <p className={"mt-4"}>{t("Profile.set-pin-code-message")}</p>
      <form onSubmit={onSubmit} className={"mt-6 flex flex-col items-center"}>
        <Controller
          name={"pin_code"}
          control={control}
          rules={{
            required: { value: true, message: t("Form.required") },
            validate: (value) => value.length === 4,
          }}
          render={({ field }) => (
            <VerifyInput
              {...field}
              placeholder={" "}
              validChars={"0-9"}
              length={4}
              classNames={{
                container: cn(
                  "h-20 w-[16.875rem] flex gap-[0.625rem] max-lg:gap-[0.313rem] max-sm:h-[4.375rem] max-md:w-[14rem]",
                ),
                character: cn(
                  "bg-surface-bright rounded-full flex items-center justify-center text-on-primary outline-transparent -outline-offset-1 outline-1 outline border-none transition-outline",
                  errors.pin_code && "outline-red-1",
                ),
                characterSelected: "pin-input bg-surface-container-high border-none transition-all outline-on-primary",
                characterFilled: "bg-surface-container-high",
              }}
            />
          )}
        />
        <div className={"mt-6 grid grid-cols-2 gap-6"}>
          <Button
            size={"medium"}
            aria-label={t("Actions.cancel")}
            onClick={onBack}
            color={"black"}
            className={"w-[14.375rem] max-md:w-full"}
          >
            {t("Actions.cancel")}
          </Button>
          <Button type={"submit"} size={"medium"} aria-label={t("Actions.confirm")} className={"w-full"}>
            {t("Actions.confirm")}
          </Button>
        </div>
      </form>
    </UpdateProfileLayout>
  );
};

export default SetCodeToProfile;
